import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  Lock,
  MapPin,
  ShieldCheck,
  KeyRound,
  Cloud,
  Signature,
  Fingerprint,
  Play,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import StoreBadges from "@/components/site/StoreBadges";
import DemoVideoCard from "@/components/site/DemoVideoCard";
import { useAppModal } from "@/context/AppModalContext";
import { useLeadForm } from "@/hooks/useLeadForm";
import { useHeroVariant } from "@/hooks/useHeroVariant";
import { trackEvent } from "@/lib/analytics";
import { useMeta } from "@/hooks/useMeta";
import { SITE } from "@/constants/testIds";
import { BRAND } from "@/lib/config";

/* , HERO , */
function Hero() {
  const variant = useHeroVariant();
  const scrollToDemo = () => {
    trackEvent("hero_demo_scroll_click", { placement: "hero" });
    const el = document.getElementById("demo");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      className="pt-14 sm:pt-20 lg:pt-24 pb-16 sm:pb-20"
      data-testid={SITE.heroSection}
      data-variant={variant}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs sm:text-sm font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
              Your personal digital blackbox
            </p>
            <h1
              className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[color:var(--yn-ink)]"
              data-testid={SITE.heroTagline}
            >
              {BRAND.tagline}
            </h1>
            <p className="mt-6 text-lg sm:text-xl leading-relaxed text-[color:var(--yn-muted)] max-w-2xl">
              YeboNadi seals what your phone captures, encrypted, time-stamped,
              GPS-locked, the moment it happens. So when it&apos;s your word
              against theirs, it isn&apos;t.
            </p>

            {variant === "B" ? <HeroVariantB scrollToDemo={scrollToDemo} /> : <HeroVariantA scrollToDemo={scrollToDemo} />}
          </div>

          <div className="lg:col-span-5">
            <div className="relative mx-auto lg:mx-0 max-w-sm">
              <div className="absolute -inset-4 rounded-3xl bg-[color:var(--yn-subtle)] -z-10" />
              <img
                src={BRAND.logoUrl}
                alt="YeboNadi, hands protecting a personal digital blackbox device"
                className="w-full rounded-2xl object-cover ring-1 ring-[color:var(--yn-border)] shadow-sm"
                loading="eager"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* Variant A, the control. Store badges + demo button. */
function HeroVariantA({ scrollToDemo }) {
  return (
    <div className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
      <StoreBadges
        placement="hero-A"
        appTestId={SITE.heroAppStoreBadge}
        playTestId={SITE.heroPlayStoreBadge}
      />
      <button
        type="button"
        onClick={scrollToDemo}
        data-testid={SITE.heroDemoBtn}
        className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md text-sm font-semibold text-[color:var(--yn-ink)] border border-[color:var(--yn-border)] bg-white hover:bg-[color:var(--yn-subtle)] transition-colors"
      >
        <Play size={16} fill="currentColor" strokeWidth={0} />
        See how it works
        <span className="text-[color:var(--yn-muted)] font-normal">(15 sec)</span>
      </button>
    </div>
  );
}

/* Variant B, email-first. Email capture is the primary block; badges below. */
function HeroVariantB({ scrollToDemo }) {
  const { email, setEmail, status, errorMsg, submit } = useLeadForm({
    source: "hero-B",
    placement: "hero-B",
  });
  return (
    <div className="mt-9 space-y-5 max-w-lg">
      <div>
        <p className="font-heading text-xl sm:text-2xl font-semibold text-[color:var(--yn-ink)] leading-snug">
          Send yourself the link. Install on the phone you actually carry.
        </p>
      </div>

      {status === "success" ? (
        <div
          className="rounded-md border border-[color:var(--yn-border)] bg-[color:var(--yn-subtle)] p-4 flex items-start gap-3"
          role="status"
          aria-live="polite"
        >
          <CheckCircle2 size={20} className="mt-0.5 shrink-0" style={{ color: "var(--yn-primary)" }} />
          <div>
            <p className="text-sm font-semibold text-[color:var(--yn-ink)]">Check your inbox.</p>
            <p className="text-sm text-[color:var(--yn-muted)] mt-1">
              App Store and Google Play links are on their way.
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-2" noValidate>
          <label htmlFor="hero-b-email" className="sr-only">
            Your email
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              id="hero-b-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              data-testid={SITE.heroEmailInput}
              className="h-12 border-[color:var(--yn-border)] focus-visible:ring-[color:var(--yn-primary)] text-base"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md text-white text-sm font-semibold bg-[color:var(--yn-primary)] hover:bg-[color:var(--yn-primary-hover)] transition-colors disabled:opacity-70 whitespace-nowrap"
              data-testid={SITE.heroEmailSubmit}
            >
              {status === "loading" && <Loader2 size={16} className="animate-spin" />}
              {status === "loading" ? "Sending…" : "Send me the link"}
            </button>
          </div>
          {status === "error" && (
            <p className="text-sm text-red-600" role="alert">
              {errorMsg}
            </p>
          )}
        </form>
      )}

      <div className="pt-1">
        <p className="text-xs font-medium uppercase tracking-wider text-[color:var(--yn-muted)] mb-2">
          Or install directly
        </p>
        <StoreBadges
          placement="hero-B"
          appTestId={SITE.heroAppStoreBadge}
          playTestId={SITE.heroPlayStoreBadge}
        />
      </div>

      <button
        type="button"
        onClick={scrollToDemo}
        data-testid={SITE.heroDemoBtn}
        className="inline-flex items-center gap-2 text-sm font-medium text-[color:var(--yn-primary)] hover:text-[color:var(--yn-primary-hover)]"
      >
        <Play size={14} fill="currentColor" strokeWidth={0} />
        See how it works (15 sec)
      </button>
    </div>
  );
}

/* , THREE MOMENTS , */
const MOMENTS = [
  {
    id: "accused",
    to: "/accused",
    testId: SITE.momentAccused,
    heading: "Accused of something you didn't do",
    emotion: "Their version keeps getting sharper each time they tell it.",
    product:
      "Yours is already sealed, with a fingerprint, a timestamp, and a location that don't move.",
  },
  {
    id: "missing",
    to: "/missing",
    testId: SITE.momentMissing,
    heading: "If something ever happens to you",
    emotion: "You may not be around to say what happened.",
    product:
      "What your phone captured is sealed and backed up, so the record survives even if you don't have your phone.",
  },
  {
    id: "altercations",
    to: "/altercations",
    testId: SITE.momentAltercations,
    heading: "When a confrontation turns",
    emotion: "The story starts rewriting itself the moment it ends.",
    product:
      "Yours is already sealed and time-stamped before anyone gets a chance to change it.",
  },
];

function ThreeMoments() {
  return (
    <section
      className="py-16 md:py-24 bg-[color:var(--yn-subtle)] border-y border-[color:var(--yn-border)]"
      data-testid={SITE.momentsSection}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
            Three moments, one product
          </p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[color:var(--yn-ink)]">
            The moments you keep hoping won&apos;t happen.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          {MOMENTS.map((m) => (
            <Link
              key={m.id}
              to={m.to}
              data-testid={m.testId}
              className="group flex flex-col justify-between rounded-2xl bg-white border border-[color:var(--yn-border)] p-6 sm:p-7 hover:border-[color:var(--yn-primary)] transition-colors"
              onClick={() =>
                trackEvent("moment_card_click", { moment: m.id })
              }
            >
              <div>
                <h3 className="font-heading text-xl sm:text-[22px] font-semibold text-[color:var(--yn-ink)] leading-snug">
                  {m.heading}
                </h3>
                <p className="mt-4 text-[15px] text-[color:var(--yn-ink)]/85 leading-relaxed">
                  {m.emotion}
                </p>
                <p className="mt-3 text-[15px] text-[color:var(--yn-muted)] leading-relaxed">
                  {m.product}
                </p>
              </div>
              <span className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--yn-primary)] group-hover:text-[color:var(--yn-primary-hover)]">
                Read more
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* , HOW IT WORKS , */
const STEPS = [
  {
    testId: SITE.howStep1,
    icon: KeyRound,
    n: "1",
    title: "Capture",
    body: "You start recording manually; the app captures audio, timestamp, and GPS until you end the session.",
  },
  {
    testId: SITE.howStep2,
    icon: Fingerprint,
    n: "2",
    title: "Sealed at the moment of capture",
    body: "Encrypted (AES-256). Fingerprinted (SHA-256). Time and location embedded. Nothing you save can be edited after it's sealed.",
  },
  {
    testId: SITE.howStep3,
    icon: ShieldCheck,
    n: "3",
    title: "Retrieve it when it matters",
    body: "Sealed recordings can't be played back inside the app. When you need one for a case, a claim, or your defense, submit a legal order or signed release along with your encryption/decryption key. YeboNadi decrypts the record and delivers it via secure cloud storage. A retrieval fee applies.",
  },
];

function HowItWorks() {
  return (
    <section
      className="py-16 md:py-24"
      data-testid={SITE.howSection}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
            How it works
          </p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[color:var(--yn-ink)]">
            Three steps. That&apos;s the whole thing.
          </h2>
        </div>

        <ol className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {STEPS.map((s) => (
            <li
              key={s.n}
              data-testid={s.testId}
              className="relative rounded-2xl border border-[color:var(--yn-border)] p-6 sm:p-7"
            >
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white font-heading font-bold text-sm"
                  style={{ background: "var(--yn-primary)" }}
                >
                  {s.n}
                </span>
                <s.icon size={20} style={{ color: "var(--yn-primary)" }} />
              </div>
              <h3 className="mt-5 font-heading text-xl font-semibold text-[color:var(--yn-ink)] leading-snug">
                {s.title}
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--yn-muted)]">
                {s.body}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* , TRUST STRIP , */
const TRUST_ITEMS = [
  { icon: Lock, label: "Sealed at capture" },
  { icon: Cloud, label: "Survives a lost phone" },
  { icon: MapPin, label: "Released only by legal order or signed release with your encryption/decryption key." },
  { icon: Signature, label: "Every user signs the Undertaking" },
];

function TrustStrip() {
  return (
    <section
      className="py-10 md:py-14 border-y border-[color:var(--yn-border)] bg-white"
      data-testid={SITE.trustStrip}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {TRUST_ITEMS.map((t) => (
            <li key={t.label}>
              <Link
                to="/security"
                onClick={() => trackEvent("trust_strip_click", { label: t.label })}
                className="group flex items-center gap-3 rounded-lg p-3 -m-3 hover:bg-[color:var(--yn-subtle)] transition-colors"
              >
                <span
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[color:var(--yn-subtle)] group-hover:bg-white transition-colors"
                >
                  <t.icon size={18} style={{ color: "var(--yn-primary)" }} />
                </span>
                <span className="text-[15px] font-medium text-[color:var(--yn-ink)] group-hover:text-[color:var(--yn-primary)] transition-colors leading-snug">
                  {t.label}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* , PRICING TEASER , */
const TIERS = [
  {
    id: "free",
    testId: SITE.pricingTierFree,
    name: "Free",
    price: "Free",
    period: "",
    blurb: "Protected from today. No credit card.",
    featured: false,
  },
  {
    id: "plus",
    testId: SITE.pricingTierPlus,
    name: "Plus",
    price: "$5.99",
    period: "/ month",
    blurb: "5 GB sealed cloud storage. Room to keep what matters.",
    featured: true,
  },
  {
    id: "pro",
    testId: SITE.pricingTierPro,
    name: "Pro",
    price: "$9.99",
    period: "/ month",
    blurb: "10 GB storage. Priority upload batching. For high-frequency use.",
    featured: false,
  },
];

function PricingTeaser() {
  return (
    <section
      className="py-16 md:py-24 bg-[color:var(--yn-subtle)] border-y border-[color:var(--yn-border)]"
      data-testid={SITE.pricingTeaser}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
              Pricing
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[color:var(--yn-ink)]">
              One product. Storage sized to you.
            </h2>
          </div>
          <Link
            to="/pricing"
            data-testid={SITE.pricingFullLink}
            onClick={() => trackEvent("pricing_view", { placement: "home-teaser" })}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--yn-primary)] hover:text-[color:var(--yn-primary-hover)]"
          >
            Full pricing
            <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          {TIERS.map((tier) => (
            <div
              key={tier.id}
              data-testid={tier.testId}
              className={`relative rounded-2xl bg-white border p-6 sm:p-7 ${
                tier.featured
                  ? "border-[color:var(--yn-primary)] shadow-sm ring-1 ring-[color:var(--yn-primary)]/10"
                  : "border-[color:var(--yn-border)]"
              }`}
            >
              {tier.featured && (
                <span
                  className="absolute -top-3 left-6 inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold text-white"
                  style={{ background: "var(--yn-primary)" }}
                >
                  Most popular
                </span>
              )}
              <p className="font-heading text-xl font-semibold text-[color:var(--yn-ink)]">
                {tier.name}
              </p>
              <div className="mt-4 flex items-baseline gap-1.5">
                <span className="font-heading text-3xl sm:text-4xl font-bold text-[color:var(--yn-ink)] tracking-tight">
                  {tier.price}
                </span>
                {tier.period && (
                  <span className="text-sm text-[color:var(--yn-muted)]">
                    {tier.period}
                  </span>
                )}
              </div>
              <p className="mt-4 text-[15px] leading-relaxed text-[color:var(--yn-muted)]">
                {tier.blurb}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* , FINAL CTA , */
function FinalCTA() {
  const { openModal } = useAppModal();
  const form = useLeadForm({ source: "home-final-cta", placement: "final-cta" });

  return (
    <section
      className="py-20 md:py-28"
      data-testid={SITE.finalCtaSection}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] text-[color:var(--yn-ink)] max-w-3xl">
          The moment you&apos;ll need it is the moment it&apos;s too late to
          install it.
        </h2>

        <div className="mt-10 space-y-6">
          <StoreBadges
            placement="final-cta"
            appTestId={SITE.finalCtaAppStoreBadge}
            playTestId={SITE.finalCtaPlayStoreBadge}
          />

          <div className="flex items-center gap-3 max-w-md">
            <div className="h-px flex-1 bg-[color:var(--yn-border)]" />
            <span className="text-xs uppercase tracking-wider text-[color:var(--yn-muted)]">
              or
            </span>
            <div className="h-px flex-1 bg-[color:var(--yn-border)]" />
          </div>

          {form.status === "success" ? (
            <div
              className="max-w-md rounded-md border border-[color:var(--yn-border)] bg-[color:var(--yn-subtle)] p-4 flex items-start gap-3"
              data-testid={SITE.finalCtaSuccess}
              role="status"
              aria-live="polite"
            >
              <CheckCircle2
                size={20}
                className="mt-0.5 shrink-0"
                style={{ color: "var(--yn-primary)" }}
              />
              <div>
                <p className="text-sm font-semibold text-[color:var(--yn-ink)]">
                  Check your inbox.
                </p>
                <p className="text-sm text-[color:var(--yn-muted)] mt-1">
                  We sent the App Store and Google Play links to your email.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={form.submit} className="max-w-md space-y-3" noValidate>
              <label
                htmlFor="final-cta-email"
                className="block text-sm font-medium text-[color:var(--yn-ink)]"
              >
                Get protected, we&apos;ll send the link to your phone.
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  id="final-cta-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => form.setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid={SITE.finalCtaEmailInput}
                  className="h-11 border-[color:var(--yn-border)] focus-visible:ring-[color:var(--yn-primary)]"
                />
                <button
                  type="submit"
                  disabled={form.status === "loading"}
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md text-white text-sm font-semibold bg-[color:var(--yn-primary)] hover:bg-[color:var(--yn-primary-hover)] transition-colors disabled:opacity-70 whitespace-nowrap"
                  data-testid={SITE.finalCtaEmailSubmit}
                >
                  {form.status === "loading" && <Loader2 size={16} className="animate-spin" />}
                  {form.status === "loading" ? "Sending…" : "Send link"}
                </button>
              </div>
              {form.status === "error" && (
                <p
                  className="text-sm text-red-600"
                  data-testid={SITE.finalCtaError}
                  role="alert"
                >
                  {form.errorMsg}
                </p>
              )}
            </form>
          )}

          <button
            type="button"
            onClick={() => openModal("home-final-cta")}
            className="text-sm font-medium text-[color:var(--yn-primary)] hover:text-[color:var(--yn-primary-hover)]"
          >
            Or open the install panel →
          </button>
        </div>
      </div>
    </section>
  );
}

/* , APP NUDGE (between pricing teaser and Final CTA) , */
function AppNudge() {
  return (
    <section className="py-14 md:py-20" data-testid="site-app-nudge">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
          The whole picture is inside the app
        </p>
        <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-tight leading-[1.1] text-[color:var(--yn-ink)]">
          Check out the App and all it can do for you.
        </h2>
        <p className="mt-4 text-lg text-[color:var(--yn-muted)] max-w-xl mx-auto">
          Register, choose a tier, and see the seal in action, from the phone
          you actually carry.
        </p>
        <p className="mt-4 text-[15px] text-[color:var(--yn-ink)]/85 max-w-2xl mx-auto leading-relaxed">
          One more thing: share your encryption/decryption key with one or
          many very trusted persons, without it, nothing you sealed can be
          retrieved, even under a legal order.
        </p>
        <div className="mt-8 flex justify-center">
          <StoreBadges
            placement="home-app-nudge"
            appTestId="site-app-nudge-app-store-badge"
            playTestId="site-app-nudge-play-store-badge"
          />
        </div>
      </div>
    </section>
  );
}

/* , PAGE , */
export default function Home() {
  useMeta({
    title: "YeboNadi, the evidence recording app that seals what your phone captures",
    description:
      "YeboNadi is a personal safety blackbox app. Encrypted, hash-sealed recordings with time and GPS, for when you need to prove what actually happened. Evidence recording that survives your phone.",
    path: "/",
  });
  return (
    <>
      <Hero />
      <DemoVideoCard />
      <ThreeMoments />
      <HowItWorks />
      <TrustStrip />
      <PricingTeaser />
      <AppNudge />
      <FinalCTA />
    </>
  );
}
